import Breadcrumbs from 'components/ui/Breadcrumbs';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import Link from 'components/ui/Link';
import config from 'config';
import { getCountryAndCity } from 'data/utils/clinic';
import { useTranslation } from 'hooks/useTranslation';
import { formatCurrency } from 'utils/currency';
import { formatNumber } from 'utils/number';
import { getQunoScoreStatus } from 'utils/qunoScore';
import { getStaffFullName } from 'utils/staff';
import Cta from 'components/ui/ContentBlocks/Cta';
import QunoscoreRating from './QunoscoreRating';
import type { DoctorProfileEntry, StartPackage } from '.';
import type { ReviewStats } from 'types/Review';

type Props = {
  entry: DoctorProfileEntry;
  reviewStats: ReviewStats;
  startPackage: StartPackage | null;
  funnelId: string;
};

const Header = ({ entry, reviewStats, startPackage }: Props): JSX.Element => {
  const {
    doctorImage,
    clinic,
    city: cityObj,
    staffTreatment,
    disableReviews,
    cta,
    qunoscoreDetails,
  } = entry;

  const qunoscore = qunoscoreDetails?.qunoscore?.['en-US'];

  const { city, country } = getCountryAndCity(cityObj);
  const { yearsOfExperience } = staffTreatment.items[0] || {};
  const { locale, t } = useTranslation();

  const breadcrumbs = [
    {
      link: entry.slug,
      title: getStaffFullName(entry, locale),
    },
  ];

  const imgWidth = doctorImage?.width || config.defaults.imageFallbackSize;

  const shouldReviewsBeVisible =
    !disableReviews &&
    reviewStats &&
    !!reviewStats.total &&
    !!reviewStats.avg_rating;

  return (
    <div>
      <div className="h-64 relative lg:hidden">
        <div className="h-64 w-full relative bg-bluishGrey">
          <div className="h-64 w-full relative bg-bluishGrey">
            <img
              className="w-full h-full object-cover"
              src={`${doctorImage?.url}?w=${840}&h=${420}&fit=fill&f=face`}
              alt={doctorImage?.description || doctorImage?.title || 'doctor'}
              width={imgWidth}
              height={256}
            />
            <div className="absolute bottom-3 left-3  bg-gray-200 !py-1 !px-3 rounded-lg border-[1px] border-[#E0E3EB]">
              {!!qunoscore && (
                <div className="flex items-center">
                  <QunoscoreRating
                    rating={qunoscore}
                    customStyle="!text-black !text-[13px] !font-bold !font-inter"
                    overrideInnerCircleColor="#52D147"
                    overrideOuterCircleColor="#C5C8D2"
                    size={42}
                    fontSize="!text-[13px]"
                  />
                  <div className="ml-1">
                    <p className="font-bold text-lg text-right text-black">
                      {t(getQunoScoreStatus(qunoscore))}
                    </p>
                    <p className="text-xs text-black -mt-1">
                      {t('doctorProfile.qunoscore')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-navy">
        <Breadcrumbs
          containerClassNames="hidden lg:block"
          className="md:pt-6 md:pb-8"
          crumbs={breadcrumbs}
          theme="light"
        />
        <div className="max-w-screen-xl mx-auto pt-6 md:pt-4 pb-8 md:pb-16 px-5 md:px-4 lg:grid md:grid-cols-3">
          <div
            className="relative hidden lg:block bg-bluishGrey overflow-hidden"
            style={{
              width: '353px',
              height: '468px',
              borderRadius: '4px 4px 4px 16px',
            }}
          >
            <Image
              fit="fill"
              fill
              src={doctorImage?.url}
              alt={doctorImage?.description || doctorImage?.title || 'doctor'}
              priority
              contentful
              className="object-cover"
            />
            <div className="absolute bottom-3 left-3 bg-gray-200 !py-1 !px-3 rounded-lg border-[1px] border-[#E0E3EB]">
              {!!qunoscore && (
                <div className="flex items-center">
                  <QunoscoreRating
                    rating={qunoscore}
                    customStyle="!text-black !font-bold !font-inter"
                    overrideInnerCircleColor="#52D147"
                    overrideOuterCircleColor="#C5C8D2"
                    size={42}
                    fontSize="!text-[13px]"
                  />
                  <div className="ml-1">
                    <p className="font-bold text-lg text-right text-black">
                      {t(getQunoScoreStatus(qunoscore))}
                    </p>
                    <p className="text-xs text-black -mt-1">
                      {t('doctorProfile.qunoscore')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2 flex gap-2 flex-col lg:pl-9">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <h1 className="text-white doctor-clinic-name py-0 mb-2">
                {getStaffFullName(entry, locale)}
              </h1>
            </div>
            {clinic && (
              <p className="text-sandBlueLighter flex text-base">
                <Icon
                  className="mr-3 lg:mr-4 shrink-0 mt-1"
                  type="LocationPinAlternate"
                  color="#7397A1"
                />

                <span>
                  {clinic.slug && ['en', 'de'].includes(locale) ? (
                    <Link href={`/${locale}/${clinic.slug}`}>
                      <a
                        className="text-sandBlueLighter underline"
                        title={clinic.name}
                      >
                        {clinic.name}
                      </a>
                    </Link>
                  ) : (
                    <>{clinic.name}</>
                  )}
                  , {city}, {country.name}
                </span>
              </p>
            )}
            {shouldReviewsBeVisible && (
              <p className="text-white text-lg flex items-center">
                <Icon type="Star" className="mr-3 lg:mr-4" color="#7397A1" />
                <span className="font-bold mr-2">
                  {formatNumber(
                    (reviewStats?.avg_rating || 0).toFixed(1),
                    locale,
                  )}
                </span>{' '}
                ({formatNumber(reviewStats?.total || 0, locale)}{' '}
                {t('doctorProfile.reviews')})
              </p>
            )}

            {yearsOfExperience && (
              <p className="text-white text-lg flex md:items-center">
                <Icon
                  className="w-4 mr-3 lg:mr-4 shrink-0"
                  type="Check"
                  color="#7397A1"
                />
                {t('doctorProfile.highlightPracticeTime')} {yearsOfExperience}
              </p>
            )}
            {startPackage && (
              <p className="text-white text-lg flex md:items-center">
                <Icon
                  className="w-4 mr-3 lg:mr-4 shrink-0"
                  type="Check"
                  color="#7397A1"
                />
                <span>
                  {startPackage.name} {t('doctorProfile.from')}
                  <span className="font-bold ml-2">
                    {formatCurrency(startPackage.price, 'EUR', locale)}
                  </span>
                </span>
              </p>
            )}
            {cta && (
              <div className="mt-3 lg:mt-8">
                <Cta {...cta} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
